import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import _ from 'lodash';
import { useIsMount } from '../../hooks/useIsMountHook';
import format from 'xml-formatter';
import { determineStringType } from '../../utils/stringUtils';

interface SmartTextAreaProps {
  text: string;
  onChange: (value: string) => void;
  label: string;
}

/**
 * A reusable text area component that accepts text and an onChange handler. Supports auto format of xml / json content.
 * @param text - Initial text content
 * @param onChange - Callback function to handle text changes
 * @param label - Label for the text area
 * @constructor
 */
const SmartTextArea = ({ text, onChange, label }: SmartTextAreaProps) => {
  const uniqueId = useMemo(() => _.uniqueId('body'), []);

  const [textAreaContent, setTextAreaContent] = useState<string>(text);
  const [textType, setTextType] = useState<string>('TEXT');
  const isMount = useIsMount();

  useEffect(() => {
    if (!isMount) {
      try {
        const type = determineStringType(textAreaContent);
        setTextType(type);
        onChange(textAreaContent);
      } catch (e) {
        setTextType('TEXT');
      }
    }
  }, [textAreaContent]);

  useEffect(() => {
    if (!isMount && textAreaContent !== text) {
      setTextAreaContent(text);
    }
  }, [text]);

  const formatContent = () => {
    if (textType === 'JSON') {
      try {
        const json = JSON.parse(textAreaContent || '');
        setTextAreaContent(JSON.stringify(json, null, 2));
      } catch (error) {
        console.error('Failed to format JSON body:', error);
      }
    } else if (textType === 'HTML') {
      const formattedHtml = format(textAreaContent || '');
      setTextAreaContent(formattedHtml);
    } else {
      console.warn('Formatting not supported for this body type:', textType);
    }
  };

  const isFormatAble = textType === 'JSON' || textType === 'HTML';

  return (
    <div>
      <div className={'d-flex justify-content-between align-items-center'}>
        <label htmlFor={uniqueId}>{label}</label>
        {isFormatAble && (
          <Button
            className={'btn-link btn-no-hover p-0 m-0'}
            variant={'link'}
            title={'Pretty Print'}
            onClick={formatContent}
          >
            {'{}'}
          </Button>
        )}
      </div>

      <Form.Control
        id={uniqueId}
        as="textarea"
        rows={10}
        name="content"
        value={textAreaContent}
        onChange={e => {
          setTextAreaContent(e.target.value);
        }}
      />
    </div>
  );
};

export default SmartTextArea;
